/**
 * Centralized display limits for the deck system.
 *
 * Item and rendering limits are generated from the canonical slide manifest so
 * backend and frontend read from the same compiled contract.
 */

import { normalizeSlideTypeForLimits as normalizeSlideType } from "./slide-type-aliases";
import {
  CHART_LIMITS as MANIFEST_CHART_LIMITS,
  INTERACTION_LIMITS as MANIFEST_INTERACTION_LIMITS,
  getManifestItemLimits,
  hasManifestSchema,
} from "./slide-manifest";

export const CHART_LIMITS = MANIFEST_CHART_LIMITS;
export const INTERACTION_LIMITS = MANIFEST_INTERACTION_LIMITS;

/**
 * Get the item limit for a specific field on a slide type.
 * Reads from the slide's JSON schema x-limits.items section.
 *
 * @example
 * getLimit('quadrant-map', 'items') // 10
 * getLimit('exec-summary', 'columns') // 3
 */
export function getLimit(slideType: string, field: string): number {
  const resolved = normalizeSlideType(slideType);
  if (!hasManifestSchema(resolved)) {
    console.warn(`Unknown slide type: ${slideType}`);
    return 10;
  }

  const itemLimits = getManifestItemLimits(resolved);
  if (!itemLimits || !(field in itemLimits)) {
    console.warn(`No item limit for '${field}' in slide type '${resolved}'`);
    return 10;
  }

  return itemLimits[field];
}

/**
 * Get chart-specific limit.
 *
 * @example
 * getChartLimit('rankingMaxSegments') // 6
 */
export function getChartLimit(field: keyof typeof CHART_LIMITS): number {
  return CHART_LIMITS[field];
}

/**
 * Clamp an array to the specified limit for a slide type.
 * Returns a new array (does not mutate).
 *
 * @example
 * clampArray(items, 'quadrant-map', 'items')
 */
export function clampArray<T>(
  arr: T[] | undefined | null,
  slideType: string,
  field: string,
): T[] {
  if (!arr) return [];
  const limit = getLimit(slideType, field);
  return arr.slice(0, limit);
}

/**
 * Check if a slide type has a limit defined for a field.
 */
export function hasItemLimit(field: string, slideType: string): boolean {
  const itemLimits = getManifestItemLimits(normalizeSlideType(slideType));
  return field in itemLimits;
}

/**
 * Get all item limits for a slide type.
 */
export function getSlideItemLimits(slideType: string): Record<string, number> {
  return getManifestItemLimits(normalizeSlideType(slideType));
}

/**
 * Supported maximum concepts for the quadrant-map combined chart + sidebar layout (CT-AUDIT-020C).
 * Equals min(schema x-limits `items`, `sidebarItems`) and matches `maxItems` on `points`, `items`,
 * and `sidebarItems`. Generator emits equal-length `points` / `sidebarItems`; UI caps with the same
 * budget and trims to the shorter array when both are non-empty (defense in depth).
 */
export function getQuadrantMapCombinedLayoutMaxConcepts(
  slideType: string,
): number {
  const resolved = normalizeSlideType(slideType);
  return Math.min(
    getLimit(resolved, "items"),
    getLimit(resolved, "sidebarItems"),
  );
}

/** Canonical cap for `quadrant-map`. */
export const QUADRANT_MAP_COMBINED_LAYOUT_MAX_CONCEPTS =
  getQuadrantMapCombinedLayoutMaxConcepts("quadrant-map");
